/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faPencil } from "@fortawesome/pro-regular-svg-icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Alert, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { createDeviceEvent } from "../../../../actions/Devices/actions";
import { DeviceEventType } from "../../../../actions/Devices/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { getFilledArrayOrDefault } from "../../../../utils";
import MetaForm from "../../../Forms/MetaForm";
import { Button } from "../../../Helper/Button";
import { toFixed } from "../../Layers/fields/formatters";
import useRipeningRoom from "../hooks/useRipeningRoom";
import { runsControllerProgram } from "../RipeningRoom/runningProgram";
import RipeningRoomEvents from "./RipeningRoomEvents";


interface RegisterValue {
    latest: number | null;
    avg: number | null;
    min: number | null;
    max: number | null;
    setpoint: number | null;
}

export interface RegisterDataProps {
    date: string;
    values?: Record<string, RegisterValue>;
    c2h4: RegisterValue;
    co2: RegisterValue;
    o2: RegisterValue;
    temperature_1: RegisterValue;
    temperature_2: RegisterValue;
}

const DEFAULT_DAY_KEYS = ["co2_value", "c2h4_value", "o2_value", "temperature_sensor_1", "temperature_sensor_2"];
const LEGACY_KEY_TO_DATA = {
    co2_value: "co2",
    c2h4_value: "c2h4",
    o2_value: "o2",
    temperature_sensor_1: "temperature_1",
    temperature_sensor_2: "temperature_2",
} as const;

interface RegisterDataComponentProps {
    isToday: boolean;
    registerData: RegisterDataProps;
}

interface RegisterDataConfigProps {
    id: string;
    name: string;
    label: string;
    unity: string;
    decimal: number;
    editable: boolean;
    type: "single-select" | "number";
    options: { label: string, value: string }[];
}

export const registerItems: Record<string, RegisterDataConfigProps> = {
    o2: {
        id: "o2",
        name: "o2",
        label: "O₂",
        type: "single-select",
        unity: "%",
        decimal: 2,
        editable: true,
        options: ["2.00", "5.00", "10.00", "15.00", "17.50", "20.00"].map((i) => ({
            label: `${i}%`,
            value: i
        }))
    },
    co2: {
        id: "co2",
        name: "co2",
        label: "CO₂",
        type: "single-select",
        unity: "%",
        decimal: 2,
        editable: true,
        options: ["0.25", "0.50", "0.75", "1.00", "1.25", "1.50", "1.75", "2.00"].map((i) => ({
            label: `${i}%`,
            value: i
        }))
    },
    c2h4: {
        id: "c2h4",
        name: "c2h4",
        label: "C₂H₄",
        type: "single-select",
        unity: "ppm",
        decimal: 0,
        editable: true,
        options: ["100", "150", "200", "250"].map((i) => ({
            label: `${i}ppm`,
            value: i
        }))
    },
    temperature_1: {
        id: "temperature_1",
        name: "temperature_1",
        label: "Temperature 1",
        type: "single-select",
        unity: "°C",
        decimal: 1,
        editable: true,
        options: ["12.0", "14.0", "16.0", "18.0", "20.0", "22.0"].map((i) => ({
            label: `${i}°C`,
            value: i
        }))
    },
    temperature_2: {
        id: "temperature_2",
        name: "temperature_2",
        label: "Temperature 2",
        type: "single-select",
        unity: "°C",
        decimal: 1,
        editable: true,
        options: ["12.0", "14.0", "16.0", "18.0", "20.0", "22.0"].map((i) => ({
            label: `${i}°C`,
            value: i
        }))
    }
};

function RegisterDataModal({ show, toggle, registerData, canEditSetpoints, setShowUpdateSetpointModal }: Omit<RegisterDataComponentProps, "isToday"> & { show: boolean; toggle: () => void, canEditSetpoints: boolean, setShowUpdateSetpointModal: (label: string) => void }) {
    const { roomEvents, availableRegisterKeys, dayViewRegisterKeys, dayViewLabelByKey } = useRipeningRoom();
    const selectedKeys = getFilledArrayOrDefault(dayViewRegisterKeys, DEFAULT_DAY_KEYS);

    const events = useMemo(() => {
        return roomEvents.filter((i) => dayjs(i.date_created).isSame(registerData.date, "day"));
    }, [roomEvents, registerData.date]);

    const labelByKey = useMemo(() => {
        const base = (availableRegisterKeys || []).reduce((acc, item) => ({ ...acc, [item.key]: item.label }), {});
        // Prefer tenant-configured day-view labels over facility register labels
        return { ...base, ...(dayViewLabelByKey || {}) };
    }, [availableRegisterKeys, dayViewLabelByKey]);
    const rowConfigs = useMemo(() => buildDisplayRows(registerData, selectedKeys, labelByKey), [registerData, selectedKeys, labelByKey]);

    const renderGasRow = (row: RegisterDataConfigProps, data: RegisterValue) => {
        return (
            <tr key={row.id}>
                <td>{row.label}</td>
                <td className="text-end">{toFixed(data.latest, row.decimal, row.unity)}</td>
                <td className="text-end">{toFixed(data.avg, row.decimal, row.unity)}</td>
                <td className="text-end">{toFixed(data.min, row.decimal, row.unity)}</td>
                <td className="text-end">{toFixed(data.max, row.decimal, row.unity)}</td>
                <td className="text-end">{toFixed(data.setpoint, row.decimal, row.unity)}</td>
                {canEditSetpoints && row.editable && <td className="text-end"><Button color="primary" size="sm" onClick={() => setShowUpdateSetpointModal(row.id)} icon={faPencil}>Edit</Button></td>}
            </tr>
        );
    };

    return (
        <Modal isOpen={show} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                Room Conditions
            </ModalHeader>
            <ModalBody>
                <div className="table-responsive">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Parameter</th>
                                <th className="text-end">Latest</th>
                                <th className="text-end">Average</th>
                                <th className="text-end">Min</th>
                                <th className="text-end">Max</th>
                                <th className="text-end">Setpoint</th>
                                {canEditSetpoints && <th className="text-end"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rowConfigs.map((row) => (
                                renderGasRow(row.item, row.data)
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="mt-3">
                    <RipeningRoomEvents events={events} />
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>Close</Button>
            </ModalFooter>
        </Modal>
    );
}

export function RegisterData({ isToday, registerData }: RegisterDataComponentProps) {
    const { availableRegisterKeys, dayViewRegisterKeys, dayViewLabelByKey, currentLocation, activeRipeningCycle } = useRipeningRoom();
    // * A room running a program gets its conditions from that program (Edit cycle -> Program);
    // * setpoints are only edited here for rooms without one.
    const canEditSetpoints = isToday && !runsControllerProgram(currentLocation, activeRipeningCycle);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showUpdateSetpointModal, setShowUpdateSetpointModal] = useState<string>("");
    const selectedKeys = getFilledArrayOrDefault(dayViewRegisterKeys, DEFAULT_DAY_KEYS);

    // Derive modal state from query parameter
    const openConditionsDate = searchParams.get("openConditionsModal");
    const showModal = openConditionsDate === registerData.date;

    // Function to close modal and clear query parameter
    const closeModal = () => {
        setSearchParams((prev) => {
            const newSearchParams = new URLSearchParams(prev);
            newSearchParams.delete("openConditionsModal");
            return newSearchParams;
        });
    };

    if (!registerData) {
        return <div className="text-center text-muted">-</div>;
    }
    const labelByKey = useMemo(() => {
        const base = (availableRegisterKeys || []).reduce((acc, item) => ({ ...acc, [item.key]: item.label }), {});
        return { ...base, ...(dayViewLabelByKey || {}) };
    }, [availableRegisterKeys, dayViewLabelByKey]);
    const rowConfigs = buildDisplayRows(registerData, selectedKeys, labelByKey);

    const renderGasRow = (row: RegisterDataConfigProps, data: RegisterValue) => {
        const value = isToday ? data.latest : data.avg;
        return (
            <div key={row.id} className="d-flex justify-content-between align-items-center">
                <span>{row.label}:</span>
                <span className="fw-bold">{toFixed(value, row.decimal, row.unity)}</span>
            </div>
        );
    };

    return (
        <>
            <div
                className="mt-2 cursor-pointer"
                onClick={() => {
                    setSearchParams((prev) => {
                        const newSearchParams = new URLSearchParams(prev);
                        newSearchParams.set("openConditionsModal", registerData.date);
                        return newSearchParams;
                    });
                }}
                css={css`&:hover { opacity: 0.8; }`}
            >
                <div className="d-flex flex-column gap-1">
                    {rowConfigs.map((row) => renderGasRow(row.item, row.data))}
                </div>
            </div>

            <RegisterDataModal
                show={showModal}
                toggle={closeModal}
                registerData={registerData}
                canEditSetpoints={canEditSetpoints}
                setShowUpdateSetpointModal={setShowUpdateSetpointModal}
            />
            <UpdateSetpointRegisterDataModal showUpdateSetpointModal={showUpdateSetpointModal} setShowUpdateSetpointModal={setShowUpdateSetpointModal} setpoint={registerData[showUpdateSetpointModal]?.setpoint} />
        </>
    );
}

function buildDisplayRows(registerData: RegisterDataProps, selectedKeys: string[], labelByKey: Record<string, string>) {
    const selected = selectedKeys.length > 0 ? selectedKeys : DEFAULT_DAY_KEYS;
    const values = registerData.values || {};
    const rows: { item: RegisterDataConfigProps; data: RegisterValue }[] = [];

    const selectedTempKeys = selected.filter((key) => key.startsWith("temperature_sensor_"));
    let temperatureRowAdded = false;

    selected.forEach((key) => {
        if (key.startsWith("temperature_sensor_")) {
            if (temperatureRowAdded) return;
            const aggregated = aggregateTemperatureValues(selectedTempKeys, values, registerData);
            if (!aggregated) return;
            rows.push({
                item: {
                    id: "temperature_setpoint",
                    name: "temperature_setpoint",
                    label: labelByKey.temperature_setpoint || "Temperature",
                    type: "single-select",
                    unity: "°C",
                    decimal: 1,
                    editable: true,
                    options: registerItems.temperature_1.options,
                },
                data: aggregated,
            });
            temperatureRowAdded = true;
            return;
        }

        const legacyKey = LEGACY_KEY_TO_DATA[key as keyof typeof LEGACY_KEY_TO_DATA];
        const data = values[key] || (legacyKey ? registerData[legacyKey] : null);
        if (!data) return;

        const knownItem = registerItems[legacyKey?.replace("temperature_", "temperature_") || key];
        const item = knownItem ? {
            ...knownItem,
            label: labelByKey[key] || knownItem.label,
        } : {
            id: key.replace("_value", ""),
            name: key.replace("_value", ""),
            label: labelByKey[key] || key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
            type: "number" as const,
            unity: "",
            decimal: 2,
            editable: false,
            options: [],
        };
        rows.push({ item, data });
    });

    return rows;
}

function aggregateTemperatureValues(selectedTempKeys: string[], values: Record<string, RegisterValue>, registerData: RegisterDataProps): RegisterValue | null {
    const tempValues = selectedTempKeys
        .map((key) => values[key])
        .filter(Boolean);

    const fallbackTemps = [
        registerData.temperature_1,
        registerData.temperature_2,
    ].filter(Boolean);

    const source = tempValues.length > 0 ? tempValues : fallbackTemps;
    if (source.length === 0) return null;

    const avg = (nums: Array<number | null | undefined>) => {
        const valid = nums.filter((num): num is number => typeof num === "number");
        if (valid.length === 0) return null;
        return valid.reduce((acc, num) => acc + num, 0) / valid.length;
    };

    return {
        latest: avg(source.map((i) => i.latest)),
        avg: avg(source.map((i) => i.avg)),
        min: avg(source.map((i) => i.min)),
        max: avg(source.map((i) => i.max)),
        setpoint: avg(source.map((i) => i.setpoint)),
    };
}


function UpdateSetpointRegisterDataModal({ showUpdateSetpointModal, setShowUpdateSetpointModal, setpoint }: { showUpdateSetpointModal: string, setShowUpdateSetpointModal: (label: string) => void, setpoint: number }) {
    const registerItem = registerItems[showUpdateSetpointModal];
    const { refreshRegisterData, currentLocation, refreshRoomEvents } = useRipeningRoom();


    const dispatch = useDispatch();
    const config = useConfig();
    const [query, setQuery] = useState({});


    const formFields = [registerItem];

    useEffect(() => {
        if (!registerItem) return;
        setQuery({
            [registerItem.id]: String(setpoint)
        });
    }, [registerItem?.id, setpoint]);


    const submitSetpoint = () => {
        if (!currentLocation || !registerItem || !query[registerItem.id]) {
            return;
        }

        // Type check to ensure currentLocation has the required properties
        const location = currentLocation as { client?: string; location_id?: string; room?: string };
        if (!location.client || !location.location_id) {
            console.error("Current location is missing required properties"); // eslint-disable-line no-console
            return;
        }

        // Create device event for setpoint update
        // For temperature sensors, use a shared setpoint register
        const registerName = registerItem.id.startsWith("temperature_")
            ? "temperature_setpoint"
            : `${registerItem.id}_setpoint`;
        dispatch(createDeviceEvent({
            device_id: location.client, // location.client == device_id
            command: DeviceEventType.WRITE_REGISTER,
            data: {
                register_name: registerName,
                value: query[registerItem.id],
                location_id: location.location_id,
                room: location.room
            }
        }) as any).then(() => {
            // Refresh register data after successful setpoint update
            refreshRegisterData();
            refreshRoomEvents();

            setShowUpdateSetpointModal(""); // Close modal
        });
    };


    return <Modal isOpen={!!showUpdateSetpointModal} toggle={() => setShowUpdateSetpointModal("")} size="lg">
        <ModalHeader toggle={() => setShowUpdateSetpointModal("")}>Update Setpoint</ModalHeader>
        {registerItem && <ModalBody>
            <Alert color="info">You are changing the cell conditions for <b>{registerItem.label}</b></Alert>
            <MetaForm
                config={config}
                object={query}
                grid={false}
                meta={formFields}
                setValue={(field, value) => setQuery((prev) => ({ ...prev, [field]: value }))}
                debounce_time={200}
            />
        </ModalBody>}
        <ModalFooter>
            <Button color="secondary" onClick={() => setShowUpdateSetpointModal("")}>Cancel</Button>
            <Button color="primary" onClick={() => submitSetpoint()} disabled={!registerItem || !query[registerItem.id] || query[registerItem.id] === setpoint}>Save</Button>
        </ModalFooter>
    </Modal>;

}
